import { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Check, ChevronDown, Plus } from 'lucide-react';
import Badge from './Badge.jsx';

/** Mirrors FormMultiSelect's trigger surface so the tag input reads as the same family of control. */
const defaultControlClass =
  'flex w-full min-h-[2.75rem] flex-wrap items-center gap-1.5 px-3 py-2 text-left text-sm leading-snug bg-surface-muted border border-edge rounded-lg text-content transition-colors hover:border-accent/50 focus-within:border-accent/50 focus-within:ring-1 focus-within:ring-accent/50';

/** Appended to listbox; `-sm` = 4px thumb. Matches FormMultiSelect scrollbar styling. */
const LISTBOX_SCROLLBAR_CLASS = 'scrollbar-aimsos-auto-sm';

/** Default max height (px) for the portaled listbox — matches `max-h-60` (15rem). */
const LISTBOX_MAX_HEIGHT_PX = 240;
const LISTBOX_GAP_PX = 4;
const LISTBOX_VIEWPORT_PAD_PX = 8;

const defaultCreateOption = (raw) => raw.trim();
const defaultFormatCreateLabel = (term) => `Create "${term}"`;

const DEFAULT_LISTBOX_CLASS =
  `fixed z-[1000] min-w-0 max-w-[min(100vw-1rem,24rem)] overflow-y-auto ${LISTBOX_SCROLLBAR_CLASS} rounded-lg border border-edge bg-surface py-1 shadow-lg`;

/** Memoized row so re-renders while typing don't rebuild every `<li>` in a long options list. */
const CreatableMultiSelectOptionRow = memo(function CreatableMultiSelectOptionRow({
  option,
  index,
  listboxId,
  isSelected,
  isActive,
  onSelect,
  onHover,
}) {
  const handleClick = useCallback(() => onSelect(option.value), [onSelect, option.value]);
  const handleMouseEnter = useCallback(() => onHover(index), [onHover, index]);

  return (
    <li
      id={`${listboxId}-opt-${index}`}
      role="option"
      aria-selected={isSelected}
      className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
        isActive ? 'bg-surface-hover' : 'hover:bg-surface-hover'
      }`}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={handleMouseEnter}
      onClick={handleClick}
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
          isSelected ? 'border-accent bg-accent' : 'border-edge bg-transparent'
        }`}
        aria-hidden
      >
        {isSelected ? <Check className="h-3 w-3 text-white" strokeWidth={3} /> : null}
      </span>
      <span className="truncate text-content">{option.label}</span>
    </li>
  );
});

CreatableMultiSelectOptionRow.propTypes = {
  option: PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
    .isRequired,
  index: PropTypes.number.isRequired,
  listboxId: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onHover: PropTypes.func.isRequired,
};

/**
 * Tag-style multi-select built on the {@link FormMultiSelect} pattern: pick from `options`
 * **and** (when `allowCreate`) create free-text tags on the fly. Selected values render as
 * removable {@link Badge} chips. Fully controlled via `value` / `onChange`.
 *
 * The options listbox portals to `document.body` (fixed) so it is not clipped by parent
 * `overflow` (e.g. `ModalLayout`). It scrolls when content exceeds the available height.
 *
 * Keyboard: type to filter; ↑/↓ move the active row; Enter selects the active option or
 * creates the typed term (explicit "Create" row); Backspace on an empty input removes the
 * last chip; Escape closes the listbox.
 *
 * @param {object} props
 * @param {string[]} props.value - Selected values (existing option values and/or created tags).
 * @param {(values: string[]) => void} props.onChange - Called with the full updated selection.
 * @param {{ value: string; label: string }[]} [props.options] - Selectable existing options.
 * @param {string} [props.placeholder] - Shown in the input when empty.
 * @param {boolean} [props.allowCreate=true] - When `false`, behaves as a searchable multi-select over `options` only.
 * @param {(rawInput: string) => string} [props.onCreateOption] - Normalizes a typed term into a value (default: trim).
 * @param {(term: string) => string} [props.formatCreateLabel] - Renders the "Create" row label (default: `Create "term"`).
 * @param {boolean} [props.disabled] - Disables the control (no open, no editing, chips not removable).
 * @param {string} [props.id] - Base id for the input; the listbox id derives from it.
 * @param {string} [props.triggerClassName] - Extra classes merged onto the control box.
 * @param {string} [props.listboxClassName] - Replaces default listbox classes; scrollbar class always appended.
 * @param {string} [props.emptyMessage='No matches'] - Shown in the listbox when there are no rows to show.
 * @param {(label: string) => string} [props.removeAriaLabel] - Builds the aria-label for a chip's remove button (default: `Remove ${label}`).
 * @param {string} [props.inputAriaLabel] - Accessible name for the combobox input; `placeholder` alone isn't a valid accessible name (default: falls back to `placeholder`).
 */
const CreatableMultiSelect = ({
  id,
  value = [],
  onChange,
  options = [],
  placeholder,
  allowCreate = true,
  onCreateOption = defaultCreateOption,
  formatCreateLabel = defaultFormatCreateLabel,
  disabled = false,
  triggerClassName,
  listboxClassName,
  emptyMessage = 'No matches',
  removeAriaLabel = (label) => `Remove ${label}`,
  inputAriaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [listboxStyle, setListboxStyle] = useState({
    position: 'fixed',
    top: 0,
    left: 0,
    width: 0,
    maxHeight: LISTBOX_MAX_HEIGHT_PX,
    zIndex: 1000,
  });
  const [listboxScrollHover, setListboxScrollHover] = useState(false);
  const containerRef = useRef(null);
  const listboxRef = useRef(null);
  const inputRef = useRef(null);
  /** True while a pointer interaction is driving open/close (avoids onFocus fighting toggle). */
  const pointerDrivenRef = useRef(false);
  const generatedId = useId();
  const inputId = id || generatedId;
  const listboxId = `${inputId}-listbox`;

  const selectedSet = useMemo(() => new Set(value), [value]);
  const labelByValue = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [options]);

  const trimmedTerm = term.trim();

  const filteredOptions = useMemo(() => {
    if (!trimmedTerm) return options;
    const needle = trimmedTerm.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, trimmedTerm]);

  const createdValue = trimmedTerm ? onCreateOption(term) : '';
  const hasExactOptionLabel = options.some(
    (o) => o.label.trim().toLowerCase() === trimmedTerm.toLowerCase(),
  );
  const hasExactOptionValue = options.some((o) => o.value === createdValue);
  const showCreateRow =
    allowCreate &&
    !!trimmedTerm &&
    !!createdValue &&
    !hasExactOptionLabel &&
    !hasExactOptionValue &&
    !selectedSet.has(createdValue);

  // Rows the keyboard navigates: filtered options first, then the optional create row.
  const rowCount = filteredOptions.length + (showCreateRow ? 1 : 0);

  const emitChange = useCallback(
    (next) => onChange?.(next),
    [onChange],
  );

  const toggleOption = useCallback(
    (optValue) => {
      const next = selectedSet.has(optValue)
        ? value.filter((v) => v !== optValue)
        : [...value, optValue];
      emitChange(next);
      setTerm('');
      setActiveIndex(0);
    },
    [emitChange, selectedSet, value],
  );

  const createFromTerm = useCallback(() => {
    if (!showCreateRow) return;
    emitChange([...value, createdValue]);
    setTerm('');
    setActiveIndex(0);
  }, [createdValue, emitChange, showCreateRow, value]);

  const removeValue = useCallback(
    (optValue) => {
      emitChange(value.filter((v) => v !== optValue));
    },
    [emitChange, value],
  );

  const updateListboxPosition = useCallback(() => {
    const trigger = containerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - LISTBOX_GAP_PX - LISTBOX_VIEWPORT_PAD_PX;
    const spaceAbove = rect.top - LISTBOX_GAP_PX - LISTBOX_VIEWPORT_PAD_PX;
    const openUp = spaceBelow < Math.min(LISTBOX_MAX_HEIGHT_PX, 120) && spaceAbove > spaceBelow;
    const available = Math.max(96, openUp ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(LISTBOX_MAX_HEIGHT_PX, available);
    const width = Math.min(rect.width, window.innerWidth - LISTBOX_VIEWPORT_PAD_PX * 2);
    const left = Math.min(
      Math.max(LISTBOX_VIEWPORT_PAD_PX, rect.left),
      window.innerWidth - width - LISTBOX_VIEWPORT_PAD_PX,
    );

    setListboxStyle({
      position: 'fixed',
      top: openUp ? 'auto' : rect.bottom + LISTBOX_GAP_PX,
      bottom: openUp ? window.innerHeight - rect.top + LISTBOX_GAP_PX : 'auto',
      left,
      width,
      maxHeight,
      zIndex: 1000,
    });
  }, []);

  const openListbox = useCallback(() => {
    if (disabled) return;
    updateListboxPosition();
    setOpen(true);
  }, [disabled, updateListboxPosition]);

  const closeListbox = useCallback(() => {
    setOpen(false);
  }, []);

  // Close on outside click (trigger + portaled listbox). Listener is deferred so the
  // same pointer gesture that opens the listbox cannot immediately dismiss it.
  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (containerRef.current?.contains(t)) return;
      if (listboxRef.current?.contains(t)) return;
      closeListbox();
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onDocMouseDown);
      document.addEventListener('touchstart', onDocMouseDown);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('touchstart', onDocMouseDown);
    };
  }, [open, closeListbox]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateListboxPosition();
    const raf = requestAnimationFrame(updateListboxPosition);
    window.addEventListener('resize', updateListboxPosition);
    window.addEventListener('scroll', updateListboxPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateListboxPosition);
      window.removeEventListener('scroll', updateListboxPosition, true);
    };
  }, [open, updateListboxPosition, rowCount, value.length, term]);

  useEffect(() => {
    if (!open) setListboxScrollHover(false);
  }, [open]);

  useEffect(() => {
    // Keep the active row in bounds as the row set changes while typing.
    setActiveIndex((i) => (i >= rowCount ? 0 : i));
  }, [rowCount]);

  const handleListboxMouseEnter = useCallback(() => setListboxScrollHover(true), []);
  const handleListboxMouseLeave = useCallback(() => setListboxScrollHover(false), []);

  const handleControlMouseDown = useCallback(
    (e) => {
      if (disabled) return;
      // Chip remove buttons own the gesture — don't toggle the listbox.
      if (e.target instanceof Element && e.target.closest('button')) return;

      // Keep focus on the input when clicking empty control area / chevron.
      if (e.target !== inputRef.current) e.preventDefault();

      // Pointer owns open/close for this gesture — onFocus must not re-open after a toggle-close.
      pointerDrivenRef.current = true;
      updateListboxPosition();

      // Clicking the text input should open (or keep open) for typing — not toggle closed.
      if (e.target === inputRef.current) {
        setOpen(true);
      } else {
        setOpen((wasOpen) => !wasOpen);
      }
      inputRef.current?.focus();
    },
    [disabled, updateListboxPosition],
  );

  const handleInputFocus = useCallback(() => {
    if (disabled) return;
    if (pointerDrivenRef.current) {
      pointerDrivenRef.current = false;
      return;
    }
    openListbox();
  }, [disabled, openListbox]);

  const activateRow = useCallback(
    (index) => {
      if (index < filteredOptions.length) {
        toggleOption(filteredOptions[index].value);
      } else if (showCreateRow) {
        createFromTerm();
      }
    },
    [createFromTerm, filteredOptions, showCreateRow, toggleOption],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      if (e.key === 'Escape') {
        closeListbox();
        return;
      }
      if (e.key === 'Backspace' && term === '' && value.length > 0) {
        e.preventDefault();
        removeValue(value[value.length - 1]);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!open) {
          openListbox();
          setActiveIndex(0);
        } else if (rowCount > 0) {
          setActiveIndex((i) => (i + 1) % rowCount);
        }
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!open) {
          openListbox();
          if (rowCount > 0) setActiveIndex(rowCount - 1);
        } else if (rowCount > 0) {
          setActiveIndex((i) => (i - 1 + rowCount) % rowCount);
        }
        return;
      }
      if (e.key === 'Enter') {
        if (!open || rowCount === 0) return;
        e.preventDefault();
        activateRow(activeIndex);
      }
    },
    [activateRow, activeIndex, closeListbox, disabled, open, openListbox, removeValue, rowCount, term, value],
  );

  const mergedControlClass = triggerClassName
    ? `${defaultControlClass} ${triggerClassName}`
    : defaultControlClass;

  const activeDescendant =
    open && rowCount > 0 ? `${listboxId}-opt-${activeIndex}` : undefined;

  const listboxNode =
    open && !disabled ? (
      <ul
        ref={listboxRef}
        id={listboxId}
        role="listbox"
        aria-multiselectable="true"
        aria-labelledby={inputId}
        data-scrollbar-hover={listboxScrollHover ? 'true' : undefined}
        onMouseEnter={handleListboxMouseEnter}
        onMouseLeave={handleListboxMouseLeave}
        style={listboxStyle}
        className={listboxClassName ? `${listboxClassName} ${LISTBOX_SCROLLBAR_CLASS}` : DEFAULT_LISTBOX_CLASS}
      >
        {filteredOptions.map((opt, index) => (
          <CreatableMultiSelectOptionRow
            key={opt.value}
            option={opt}
            index={index}
            listboxId={listboxId}
            isSelected={selectedSet.has(opt.value)}
            isActive={index === activeIndex}
            onSelect={toggleOption}
            onHover={setActiveIndex}
          />
        ))}

        {showCreateRow ? (
          <li
            id={`${listboxId}-opt-${filteredOptions.length}`}
            role="option"
            aria-selected={false}
            className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
              activeIndex === filteredOptions.length ? 'bg-surface-hover' : 'hover:bg-surface-hover'
            }`}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setActiveIndex(filteredOptions.length)}
            onClick={createFromTerm}
          >
            <Plus className="h-4 w-4 shrink-0 text-accent" aria-hidden />
            <span className="truncate text-content">{formatCreateLabel(trimmedTerm)}</span>
          </li>
        ) : null}

        {rowCount === 0 ? (
          <li className="px-3 py-2 text-sm text-content-muted" aria-disabled="true">
            {emptyMessage}
          </li>
        ) : null}
      </ul>
    ) : null;

  return (
    <div ref={containerRef} className="relative">
      {/* Control box: chips + text input. Clicking it focuses the input. */}
      <div
        className={`${mergedControlClass} ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-text'}`}
        onMouseDown={handleControlMouseDown}
      >
        {value.map((v) => (
          <Badge
            key={v}
            size="small"
            variant="neutral"
            closable={!disabled}
            disabled={disabled}
            id={v}
            onClose={removeValue}
            closeButtonAriaLabel={removeAriaLabel(labelByValue.get(v) ?? v)}
          >
            {labelByValue.get(v) ?? v}
          </Badge>
        ))}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          disabled={disabled}
          autoComplete="off"
          aria-label={inputAriaLabel ?? placeholder}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
          placeholder={value.length === 0 ? placeholder : undefined}
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setActiveIndex(0);
            openListbox();
          }}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className="min-w-[6rem] flex-1 bg-transparent text-sm text-content outline-none placeholder:text-content-muted disabled:cursor-not-allowed"
        />
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-content-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </div>

      {typeof document !== 'undefined' && listboxNode
        ? createPortal(listboxNode, document.body)
        : null}
    </div>
  );
};

CreatableMultiSelect.propTypes = {
  id: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
  ),
  placeholder: PropTypes.string,
  allowCreate: PropTypes.bool,
  onCreateOption: PropTypes.func,
  formatCreateLabel: PropTypes.func,
  disabled: PropTypes.bool,
  triggerClassName: PropTypes.string,
  listboxClassName: PropTypes.string,
  emptyMessage: PropTypes.string,
  removeAriaLabel: PropTypes.func,
  inputAriaLabel: PropTypes.string,
};

export default CreatableMultiSelect;
